import React, { useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthProvider';
import '../css/ProfilePage.css';
import axios from 'axios';

const ProfilePage = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [storedUsername, setStoredUsername] = useState(() => localStorage.getItem('username') || '');
  const connectedParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('connected') || '';
  }, [location.search]);
  const defaultEmail = useMemo(() => {
    const saved = localStorage.getItem('profile_email');
    if (saved) return saved;
    if (storedUsername) {
      return `${storedUsername.toLowerCase()}@example.com`;
    }
    return 'user@example.com';
  }, [storedUsername]);

  const [fullName, setFullName] = useState(localStorage.getItem('profile_fullname') || storedUsername || 'User');
  const [email, setEmail] = useState(defaultEmail);
  const [timezone, setTimezone] = useState(localStorage.getItem('profile_timezone') || '(GMT-05:00) Eastern Time (US & Canada)');
  const [reports, setReports] = useState(true);
  const [updates, setUpdates] = useState(true);
  const [saved, setSaved] = useState('');
  const [socialStatus, setSocialStatus] = useState({ accounts: [], preferences: null });
  const [routingStatus, setRoutingStatus] = useState('');
  const [removingPageKey, setRemovingPageKey] = useState('');
  const [securityMsg, setSecurityMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';
  const FB2_ENABLED = String(process.env.REACT_APP_FB2_CONNECT_ENABLED || '').trim() === '1';
  const FB2_PROVIDER = process.env.REACT_APP_FB2_PROVIDER || 'facebook2';
  const FB2_LABEL = process.env.REACT_APP_FB2_LABEL || 'Connect Second Facebook App';
  const effectiveUsername = storedUsername || 'User';
  const routingMap = useMemo(() => {
    const map = new Map();
    (socialStatus.accounts || []).forEach((account) => {
      const key = `${account.provider}:${account.page_id || account.account_id}`;
      const routing = account.routing || {};
      map.set(key, {
        categories: Array.isArray(routing.categories) ? routing.categories : [],
        subcategories: Array.isArray(routing.subcategories) ? routing.subcategories : [],
      });
    });
    return map;
  }, [socialStatus.accounts]);
  const [routingEdits, setRoutingEdits] = useState({});

  useEffect(() => {
    const syncUsername = () => {
      const current = localStorage.getItem('username') || '';
      if (current && current !== storedUsername) {
        setStoredUsername(current);
        setFullName(current);
      }
    };
    syncUsername();
    const onStorage = (event) => {
      if (event.key === 'username') {
        syncUsername();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storedUsername]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (!effectiveUsername || effectiveUsername === 'User') {
          setSocialStatus({ accounts: [], preferences: null });
          return;
        }
        const response = await axios.get(
          `${BASE_URL}/social/status?username=${encodeURIComponent(effectiveUsername)}`,
          { headers: { 'x-user': effectiveUsername } }
        );
        const data = response.data || {};
        setSocialStatus({ accounts: data.accounts || [], preferences: data.preferences || null });
        if (data.preferences) {
          setSocialStatus((prev) => ({ ...prev, preferences: data.preferences }));
        }
      } catch (error) {
        console.error('Failed to load social status');
      }
    };
    fetchStatus();
  }, [BASE_URL, effectiveUsername, connectedParam]);

  useEffect(() => {
    const next = {};
    (socialStatus.accounts || [])
      .filter((account) => account.provider === 'facebook')
      .forEach((account) => {
        const key = `${account.provider}:${account.page_id || account.account_id}`;
        const current = routingMap.get(key) || { categories: [], subcategories: [] };
        next[key] = {
          categories: current.categories.join(', '),
          subcategories: current.subcategories.join(', '),
        };
      });
    setRoutingEdits(next);
  }, [routingMap, socialStatus.accounts]);

  const onSave = (e) => {
    e.preventDefault();
    const nextName = fullName || storedUsername;
    if (nextName) {
      localStorage.setItem('username', nextName);
      localStorage.setItem('profile_fullname', nextName);
      setStoredUsername(nextName);
    }
    if (email) {
      localStorage.setItem('profile_email', email);
    }
    if (timezone) {
      localStorage.setItem('profile_timezone', timezone);
    }
    setSaved('Settings saved successfully.');
    setTimeout(() => setSaved(''), 2000);
  };

  const openConnect = (provider) => {
    window.location.href = `${BASE_URL}/social/${provider}/connect?username=${encodeURIComponent(effectiveUsername)}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSecurityUpdate = (e) => {
    e.preventDefault();
    if (!nextPassword || nextPassword !== confirmPassword) {
      setSecurityMsg('New passwords do not match.');
      return;
    }
    setSecurityMsg('Security details updated.');
    setCurrentPassword('');
    setNextPassword('');
    setConfirmPassword('');
    setTimeout(() => setSecurityMsg(''), 2500);
  };

  const updateRouting = async (account, nextCategories, nextSubcategories) => {
    setRoutingStatus('Saving page routing...');
    try {
      await axios.post(
        `${BASE_URL}/social/page-routing`,
        {
          username: effectiveUsername,
          provider: account.provider,
          page_id: account.page_id || account.account_id,
          categories: nextCategories,
          subcategories: nextSubcategories,
        },
        { headers: { 'Content-Type': 'application/json', 'x-user': effectiveUsername } }
      );
      setRoutingStatus('Page routing saved.');
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save routing.';
      setRoutingStatus(message);
    }
    setTimeout(() => setRoutingStatus(''), 2500);
  };

  const updateRoutingEdit = (key, field, value) => {
    setRoutingEdits((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const removeConnectedPage = async (account) => {
    const pageKey = `${account.provider}:${account.page_id || account.account_id}`;
    setRemovingPageKey(pageKey);
    setRoutingStatus('Removing page...');
    try {
      await axios.post(
        `${BASE_URL}/social/remove-account`,
        {
          username: effectiveUsername,
          provider: account.provider,
          page_id: account.page_id || account.account_id,
        },
        { headers: { 'Content-Type': 'application/json', 'x-user': effectiveUsername } }
      );

      setSocialStatus((prev) => ({
        ...prev,
        accounts: (prev.accounts || []).filter(
          (item) =>
            !(
              item.provider === account.provider &&
              String(item.page_id || item.account_id) === String(account.page_id || account.account_id)
            )
        ),
      }));
      setRoutingStatus('Page removed.');
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to remove page.';
      setRoutingStatus(message);
    } finally {
      setRemovingPageKey('');
      setTimeout(() => setRoutingStatus(''), 2500);
    }
  };

  const applyPreset = (key, preset) => {
    if (preset === 'usa') {
      setRoutingEdits((prev) => ({
        ...prev,
        [key]: {
          categories: 'News, World',
          subcategories: 'US, World, General',
        },
      }));
    }
    if (preset === 'football') {
      setRoutingEdits((prev) => ({
        ...prev,
        [key]: {
          categories: 'Football',
          subcategories: 'Football News, Transfers, Soccer News',
        },
      }));
    }
    if (preset === 'ronaldo') {
      setRoutingEdits((prev) => ({
        ...prev,
        [key]: {
          categories: 'Cristiano Ronaldo',
          subcategories: 'Cristiano Ronaldo',
        },
      }));
    }
    if (preset === 'finance') {
      setRoutingEdits((prev) => ({
        ...prev,
        [key]: {
          categories: 'Finance',
          subcategories: 'Finance News',
        },
      }));
    }
    if (preset === 'manual') {
      setRoutingEdits((prev) => ({
        ...prev,
        [key]: {
          categories: 'Manual',
          subcategories: 'Manual Links',
        },
      }));
    }
  };

  return (
    <div className="profile-shell">
      <div className="profile-card">
        <aside className="profile-sidebar">
          <h2>BlinkURL</h2>
          <button className="menu-item active">Profile</button>
          <button className="menu-item">Security</button>
          <button className="menu-item logout" onClick={handleLogout}>Log Out</button>
        </aside>

        <section className="profile-main">
          <header>
            <h1>Account Settings</h1>
            <p>Manage your account settings.</p>
          </header>

          <form className="profile-form" onSubmit={onSave}>
            <h3>Profile Information</h3>
            <label>Full Name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <label>Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label>Time Zone</label>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              <option>(GMT-05:00) Eastern Time (US & Canada)</option>
              <option>(GMT+00:00) UTC</option>
              <option>(GMT+01:00) Central European Time</option>
            </select>
            <button type="submit" className="save-btn">Save Changes</button>
            {saved && <p className="saved-msg">{saved}</p>}
          </form>

          <form className="profile-form" onSubmit={handleSecurityUpdate}>
            <h3>Security</h3>
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
            <label>New Password</label>
            <input
              type="password"
              value={nextPassword}
              onChange={(e) => setNextPassword(e.target.value)}
              placeholder="Create a new password"
            />
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
            <button type="submit" className="save-btn">Update Security</button>
            {securityMsg && <p className="saved-msg">{securityMsg}</p>}
          </form>

          <div className="profile-notifications">
            <h3>Email Notifications</h3>
            <div className="switch-row">
              <div>
                <strong>Receive activity reports</strong>
                <p>Get weekly analytics summary.</p>
              </div>
              <input type="checkbox" checked={reports} onChange={() => setReports(!reports)} />
            </div>
            <div className="switch-row">
              <div>
                <strong>Send me product updates</strong>
                <p>Receive news and feature updates.</p>
              </div>
              <input type="checkbox" checked={updates} onChange={() => setUpdates(!updates)} />
            </div>
          </div>

          <div className="profile-notifications">
            <h3>Social Connections</h3>
            <div className="switch-row">
              <div>
                <strong>Facebook Pages</strong>
                <p>{socialStatus.accounts.some((a) => a.provider === 'facebook') ? 'Connected' : 'Not connected'}</p>
              </div>
              <div className="switch-row-actions">
                <button type="button" className="save-btn" onClick={() => openConnect('facebook')}>Connect</button>
                <button
                  type="button"
                  className="save-btn secondary-btn"
                  onClick={() => openConnect(FB2_PROVIDER)}
                  disabled={!FB2_ENABLED}
                >
                  {FB2_LABEL}
                </button>
              </div>
            </div>
            {!FB2_ENABLED && (
              <p className="switch-note">Second Facebook app is not active yet.</p>
            )}
            <div className="switch-row">
              <div>
                <strong>X (Twitter)</strong>
                <p>{socialStatus.accounts.some((a) => a.provider === 'x') ? 'Connected' : 'Not connected'}</p>
              </div>
              <button type="button" className="save-btn" onClick={() => openConnect('x')}>Connect</button>
            </div>
          </div>

          <div className="profile-notifications">
            <h3>Page Routing</h3>
            <p>Select where each page should receive posts.</p>
            <div className="routing-table">
              <div className="routing-row routing-head">
                <span>Provider</span>
                <span>Page</span>
                <span>Categories</span>
                <span>Subcategories</span>
                <span>Save</span>
                <span>Remove</span>
              </div>
              {(socialStatus.accounts || [])
                .filter((account) => account.provider === 'facebook')
                .map((account) => {
                  const key = `${account.provider}:${account.page_id || account.account_id}`;
                  const current = routingEdits[key] || { categories: '', subcategories: '' };

                  return (
                    <div className="routing-row" key={key}>
                      <span className="routing-provider">{account.provider}</span>
                      <span className="routing-page-name">{account.page_name || account.page_id}</span>
                      <input
                        className="routing-input"
                        value={current.categories}
                        onChange={(e) => updateRoutingEdit(key, 'categories', e.target.value)}
                        placeholder="News, Football"
                      />
                      <input
                        className="routing-input"
                        value={current.subcategories}
                        onChange={(e) => updateRoutingEdit(key, 'subcategories', e.target.value)}
                        placeholder="World, Transfers"
                      />
                      <button
                        type="button"
                        className="save-btn routing-action-btn"
                        onClick={() =>
                          updateRouting(
                            account,
                            current.categories.split(',').map((v) => v.trim()).filter(Boolean),
                            current.subcategories.split(',').map((v) => v.trim()).filter(Boolean)
                          )
                        }
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="preset-btn routing-action-btn routing-remove-btn"
                        onClick={() => removeConnectedPage(account)}
                        disabled={removingPageKey === key}
                      >
                        {removingPageKey === key ? 'Removing...' : 'Remove'}
                      </button>
                      <div className="preset-row">
                        <button type="button" className="preset-btn" onClick={() => applyPreset(key, 'usa')}>
                          USA News
                        </button>
                        <button type="button" className="preset-btn" onClick={() => applyPreset(key, 'football')}>
                          Football
                        </button>
                        <button type="button" className="preset-btn" onClick={() => applyPreset(key, 'ronaldo')}>
                          Cristiano Ronaldo
                        </button>
                        <button type="button" className="preset-btn" onClick={() => applyPreset(key, 'finance')}>
                          Finance
                        </button>
                        <button type="button" className="preset-btn" onClick={() => applyPreset(key, 'manual')}>
                          Manual
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
            {routingStatus && <p className="saved-msg">{routingStatus}</p>}
          </div>

        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
